import { CircleButton } from "components/CircleButton";

import { Box, Typography, useTheme } from "@mui/material";
import { MAX_SLIPPAGE, MAX_VALIDATOR_TIP, MIN_VALIDATOR_TIP } from "consts";
import { MIN_SLIPPAGE } from "consts";
import { Close } from "@mui/icons-material";
import { DarkDrawer } from "components/TradeDrawer";
import { StyledInputV2 } from "components/CrossChainSwapDrawerV2/styled";
import { RetroButton } from "components/RetroButton";
import { SLIPPAGE_OPTIONS, VALIDATOR_TIP_OPTIONS } from "../../utils/configs";

const PreferencesDrawer = ({
  isOpen,
  close,
  slippageRef,
  handleSlippageChange,
  slippage,
  showValidatorTip,
  validatorTipRef,
  handleValidatorTipChange,
  validatorTip,
  isShowTips,
  isSlippageValid,
  handleConfirmPreferences,
}: {
  isOpen: boolean;
  close: () => void;
  slippageRef: React.RefObject<HTMLInputElement>;
  handleSlippageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  slippage: number;
  showValidatorTip: boolean;
  validatorTipRef: React.RefObject<HTMLInputElement>;
  handleValidatorTipChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  validatorTip: number;
  isShowTips: boolean;
  isSlippageValid: boolean;
  handleConfirmPreferences: () => void;
}) => {
  const theme = useTheme();
  return (
    <DarkDrawer open={isOpen} anchor="bottom" onClose={() => {}} sx={{ zIndex: 2000 }}>
      {/* Slippage */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
        <Typography variant="title-h2">Preferences</Typography>
        <Box>
          <CircleButton variant="white" size="lg" onClick={close}>
            <Close />
          </CircleButton>
        </Box>
      </Box>
      <Box sx={{ marginTop: "32px" }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
        <Typography variant="subtitle">Slippage</Typography>
      </Box>
      <Box sx={{ marginTop: "8px" }}>
        <Box sx={{ position: "relative" }}>
          <StyledInputV2
            type="number"
            min={MIN_SLIPPAGE}
            max={MAX_SLIPPAGE}
            step="0.1"
            ref={slippageRef}
            onChange={handleSlippageChange}
            defaultValue={slippage}
          />
          <Typography
            variant="body-md"
            color={theme.palette.text.secondary}
            sx={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
            }}>
            %
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
        {SLIPPAGE_OPTIONS.map((option) => (
          <OptionButton
            key={option.value}
            label={option.label}
            onClick={() => {
              slippageRef.current!.value = option.value.toString();
              handleSlippageChange({
                target: { value: option.value.toString() },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            isDefault={option.isDefault}
          />
        ))}
      </Box>
      <Box sx={{ minHeight: "16px", height: "fit-content", opacity: 0, pointerEvents: "none" }}>
        <Typography variant="body-sm" color={theme.palette.text["system-red"]}>
          To ensure the transaction is successful please set the priority fee higher than the
          validator tip.
        </Typography>
      </Box>
      <Box sx={{ marginTop: "16px" }} />

      {/* Validator Tip */}
      {showValidatorTip ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Typography variant="subtitle">Validator Tip</Typography>
          </Box>
          <Box sx={{ marginTop: "8px" }}>
            <Box sx={{ position: "relative" }}>
              <StyledInputV2
                type="number"
                min={MIN_VALIDATOR_TIP}
                max={MAX_VALIDATOR_TIP}
                step="0.001"
                ref={validatorTipRef}
                onChange={handleValidatorTipChange}
                defaultValue={validatorTip}
              />
              <Typography
                variant="body-md"
                color={theme.palette.text.secondary}
                sx={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}>
                SOL
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
            {VALIDATOR_TIP_OPTIONS.map((option) => (
              <OptionButton
                key={option.value}
                label={option.label}
                onClick={() => {
                  validatorTipRef.current!.value = option.value.toString();
                  handleValidatorTipChange({
                    target: { value: option.value.toString() },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                isDefault={option.isDefault}
              />
            ))}
          </Box>
          <Box
            sx={{
              minHeight: "16px",
              height: "fit-content",
              opacity: isShowTips ? 1 : 0,
              pointerEvents: isShowTips ? "auto" : "none",
            }}>
            <Typography variant="body-sm" color={theme.palette.text["system-red"]}>
              To ensure the transaction is successful please set the priority fee higher than the
              validator tip.
            </Typography>
          </Box>
          <Box sx={{ marginTop: "16px" }} />
        </>
      ) : null}
      {/* Confirm */}
      <Box>
        <RetroButton
          variant="white"
          onClick={handleConfirmPreferences}
          disabled={!isSlippageValid /*  || !isValidatorTipValid || !isPriorityFeeValid */}>
          <Typography variant="title-h3" sx={{ paddingY: theme.spacing(1) }}>
            SAVE
          </Typography>
        </RetroButton>
      </Box>
      <Box
        sx={{
          marginTop: "64px",
        }}
      />
    </DarkDrawer>
  );
};

const OptionButton = ({
  label,
  onClick,
  isDefault,
}: {
  label: string;
  onClick: () => void;
  isDefault: boolean;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}>
      <RetroButton variant="primary" onClick={onClick}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 12px",
          }}>
          <Typography variant="button-md" sx={{ display: "inline-block", whiteSpace: "nowrap" }}>
            {label}
            {isDefault ? ` (Default)` : ""}
          </Typography>
        </Box>
      </RetroButton>
    </Box>
  );
};

export { PreferencesDrawer };
